// src/pages/AIReportingPage.js

import React, { useState, useEffect, useMemo } from 'react'
import {
  Container,
  Typography,
  Box,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Snackbar,
  Alert,
  Autocomplete,
  TextField,
  Stack,
  Button,
  Divider
} from '@mui/material'
import CommentIcon from '@mui/icons-material/Comment'
import {
  collection,
  query,
  where,
  getDocs,
  onSnapshot,
  doc,
  updateDoc,
  deleteField
} from 'firebase/firestore'
import { db } from '../firebase'
import { useAuth } from '../contexts/AuthContext'

// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

import { Document, Paragraph, Packer } from 'docx';

export default function AIReportingPage() {
  const { currentUser, userRole } = useAuth()
  const { t } = useTranslation()

  // --------------------------------------------------------------------------
  // STATE
  // --------------------------------------------------------------------------
  const [workspaces, setWorkspaces] = useState([])
  const [selectedWorkspace, setSelectedWorkspace] = useState(null)
  const [workspaceLoading, setWorkspaceLoading] = useState(true)

  const [channels, setChannels] = useState([])
  const [selectedChannels, setSelectedChannels] = useState([])
  const [channelsLoading, setChannelsLoading] = useState(false)

  const [questionForms, setQuestionForms] = useState([])
  const [selectedForms, setSelectedForms] = useState([])
  const [formsLoading, setFormsLoading] = useState(false)

  const [availableTags, setAvailableTags] = useState([])
  const [selectedTags, setSelectedTags] = useState([])

  const [rawRows, setRawRows] = useState([])
  const [loadingAnswers, setLoadingAnswers] = useState(false)

  // Kullanıcı email/nickname bilgileri
  const [usersMap, setUsersMap] = useState({})

  // AI parametreleri
  const [AIQuestion, setAIQuestion] = useState('')
  const [AISumText, setAISumText] = useState('')
  const [AIReport, setAIReport] = useState('')

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  })
  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false })

  const [apiLoading, setApiLoading] = useState(false)

  const [availableQuestions, setAvailableQuestions] = useState([])
  const [selectedQuestions, setSelectedQuestions] = useState([])

  // --------------------------------------------------------------------------
  // HELPER
  // --------------------------------------------------------------------------
  const stripHtmlTags = (htmlString = '') => {
    const div = document.createElement('div')
    div.innerHTML = htmlString
    return div.textContent || div.innerText || ''
  }

  // --------------------------------------------------------------------------
  // 0) Tüm kullanıcıları çek -> usersMap
  // --------------------------------------------------------------------------
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const userSnap = await getDocs(collection(db, 'users'))
        const tempMap = {}
        userSnap.forEach((docSnap) => {
          const data = docSnap.data()
          tempMap[docSnap.id] = {
            email: data.email || docSnap.id,
            nickname: data.nickname || 'NoNickname'
          }
        })
        setUsersMap(tempMap)
      } catch (error) {
        console.error('users alınırken hata:', error)
      }
    }
    fetchAllUsers()
  }, [])

  // --------------------------------------------------------------------------
  // 1) Workspace listesi
  // --------------------------------------------------------------------------
  useEffect(() => {
    const wsRef = collection(db, 'workspaces')
    const unsubscribe = onSnapshot(
      wsRef,
      async (snapshot) => {
        try {
          let wsData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data()
          }))

          if (userRole === 'admin') {
            setWorkspaces(wsData)
            setWorkspaceLoading(false)
          } else {
            const allWsIds = wsData.map((ws) => ws.id)
            if (allWsIds.length === 0) {
              setWorkspaces([])
              setWorkspaceLoading(false)
              return
            }
            const channelsRef = collection(db, 'channels')
            const qCh = query(channelsRef, where('workspaceId', 'in', allWsIds))
            const chSnap = await getDocs(qCh)

            const relevantWsIds = new Set()
            chSnap.forEach((chDoc) => {
              const chData = chDoc.data()
              if (
                chData.memberDetails &&
                chData.memberDetails.some(
                  (md) => md.userId === currentUser.uid && md.role === 'moderator'
                )
              ) {
                relevantWsIds.add(chData.workspaceId)
              }
            })

            wsData = wsData.filter((ws) => relevantWsIds.has(ws.id))
            setWorkspaces(wsData)
            setWorkspaceLoading(false)
          }
        } catch (err) {
          console.error('Workspace okunurken hata:', err)
          setSnackbar({
            open: true,
            message: t('aiReporting.workspaceFetchError'),
            severity: 'error'
          })
          setWorkspaceLoading(false)
        }
      },
      (err) => {
        console.error('Workspace okunurken hata (snapshot):', err)
        setSnackbar({
          open: true,
          message: t('aiReporting.workspaceSnapshotError'),
          severity: 'error'
        })
        setWorkspaceLoading(false)
      }
    )

    return () => unsubscribe()
  }, [userRole, currentUser, t])

  // --------------------------------------------------------------------------
  // 2) Workspace seçilince -> moderatör kanalları
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedWorkspace) {
      setChannels([])
      setSelectedChannels([])
      return
    }

    const fetchChannels = async () => {
      try {
        setChannelsLoading(true)
        const chRef = collection(db, 'channels')
        const qChannel = query(chRef, where('workspaceId', '==', selectedWorkspace.id))
        const chSnap = await getDocs(qChannel)
        const allChannels = chSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))

        // Moderator => filtre
        const modChannels = allChannels.filter((ch) => {
          if (!ch.memberDetails) return false
          return ch.memberDetails.some(
            (md) => md.userId === currentUser.uid && md.role === 'moderator'
          )
        })

        setChannels(modChannels)
        setChannelsLoading(false)
        setAvailableTags(selectedWorkspace.tags || [])
        setSelectedTags([])
      } catch (err) {
        console.error('Kanal listesi alınırken hata:', err)
        setSnackbar({
          open: true,
          message: t('aiReporting.noChannelsFound'),
          severity: 'error'
        })
        setChannelsLoading(false)
      }
    }

    fetchChannels()
  }, [selectedWorkspace, currentUser, t])

  // --------------------------------------------------------------------------
  // 3) Çoklu kanal seçilince -> çoklu form listesi
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!selectedChannels || selectedChannels.length === 0) {
      setQuestionForms([])
      setSelectedForms([])
      return
    }

    const fetchForms = async () => {
      try {
        setFormsLoading(true)
        const channelIds = selectedChannels.map((ch) => ch.id)
        const ref = collection(db, 'questionForms')
        const qForms = query(ref, where('channelId', 'in', channelIds))
        const formsSnap = await getDocs(qForms)
        const formsData = formsSnap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }))
        setQuestionForms(formsData)
        setFormsLoading(false)
      } catch (error) {
        console.error('Form listesi alınırken hata:', error)
        setSnackbar({
          open: true,
          message: t('aiReporting.noFormsFound'),
          severity: 'error'
        })
        setFormsLoading(false)
      }
    }

    fetchForms()
  }, [selectedChannels, t])

  // --------------------------------------------------------------------------
  // 4) Seçilen formların cevaplarını çek -> rawRows
  // --------------------------------------------------------------------------
  useEffect(() => {
    const fetchAnswers = async () => {
      if (!selectedChannels.length || !selectedForms.length) {
        setRawRows([])
        return
      }

      try {
        setLoadingAnswers(true)
        const newRawRows = []

        for (let ch of selectedChannels) {
          for (let f of selectedForms) {
            if (f.channelId !== ch.id) continue

            const respRef = collection(db, 'responses')
            const qResp = query(
              respRef,
              where('channelId', '==', ch.id),
              where('questionFormId', '==', f.id)
            )
            const respSnap = await getDocs(qResp)
            const rawResponses = respSnap.docs.map((docSnap) => ({
              docId: docSnap.id,
              ...docSnap.data()
            }))

            const userTagMap = {}
            if (ch.memberDetails) {
              ch.memberDetails.forEach((md) => {
                userTagMap[md.userId] = md.tags || []
              })
            }

            rawResponses.forEach((resp) => {
              const { userId, finalAnswers, createdAt } = resp
              const userNickname = usersMap[userId]?.nickname || 'NoNickname'
              const userTags = userTagMap[userId] || []
              const dateStr = createdAt?.toDate
                ? createdAt.toDate().toLocaleString()
                : ''

              if (!Array.isArray(finalAnswers)) return

              finalAnswers.forEach((fa, idx) => {
                let textAnswer = ''
                let mediaList = []
                let videoUrl = ''

                if (typeof fa.answer === 'object' && fa.answer !== null) {
                  if (fa.answer.media && Array.isArray(fa.answer.media)) {
                    textAnswer = fa.answer.text || ''
                    mediaList = fa.answer.media.map((mObj) => ({
                      type: mObj.fileType || 'image',
                      url: mObj.remoteUrl
                    }))
                  } else if (fa.answer.videoUrl) {
                    videoUrl = fa.answer.videoUrl
                  } else if (fa.answer.collageSingleUrl) {
                    mediaList = [
                      {
                        type: 'image',
                        url: fa.answer.collageSingleUrl
                      }
                    ]
                  } else if (Array.isArray(fa.answer)) {
                    // AI-Moderator object array check
                    if (
                      fa.answer.length > 0 &&
                      fa.answer.every(
                        (msgObj) =>
                          msgObj &&
                          typeof msgObj === 'object' &&
                          'role' in msgObj &&
                          'content' in msgObj
                      )
                    ) {
                      textAnswer = fa.answer
                        .map((msgObj) => `[${msgObj.role}] ${msgObj.content}`)
                        .join('\n')
                    } else {
                      textAnswer = fa.answer.join(', ')
                    }
                  } else if (typeof fa.answer.text === 'string') {
                    textAnswer = fa.answer.text
                  }
                } else if (Array.isArray(fa.answer)) {
                  textAnswer = fa.answer.join(', ')
                } else if (typeof fa.answer === 'string') {
                  textAnswer = fa.answer
                }

                newRawRows.push({
                  id: resp.docId + '_' + idx,
                  docId: resp.docId,
                  userId,
                  userNickname,
                  userTags,
                  questionText: fa.questionText,
                  textAnswer,
                  mediaList,
                  videoUrl,
                  createdAt: dateStr,
                  channelName: ch.name,
                  formName: f.name
                })
              })
            })
          }
        }

        setRawRows(newRawRows)
        setLoadingAnswers(false)
      } catch (error) {
        console.error('Cevaplar alınırken hata:', error)
        setSnackbar({
          open: true,
          message: t('aiReporting.noAnswersFound'),
          severity: 'error'
        })
        setLoadingAnswers(false)
      }
    }

    fetchAnswers()
  }, [selectedChannels, selectedForms, usersMap, t])

  // --------------------------------------------------------------------------
  // Seçili tag’lere göre filtrelenmiş cevaplar
  // --------------------------------------------------------------------------
  const filteredRawRows = useMemo(() => {
    let filtered = rawRows
    
    // Tag filtre
    if (selectedTags.length > 0) {
      filtered = filtered.filter((r) => {
        return selectedTags.some((tag) => r.userTags.includes(tag))
      })
    }
    
    // Soru filtre
    if (selectedQuestions.length > 0) {
      const selectedSet = new Set(selectedQuestions);
      filtered = filtered.filter(r => selectedSet.has(r.questionText));
    }
  
    // Orijinal sırayı korumak için rawRows'taki sırayı kullan
    return filtered.sort((a, b) => {
      const aIndex = rawRows.findIndex(row => row.id === a.id);
      const bIndex = rawRows.findIndex(row => row.id === b.id);
      return aIndex - bIndex;
    });
    
  }, [rawRows, selectedTags, selectedQuestions])

  useEffect(() => {
    const fetchQuestions = async () => {
      if (!selectedForms || selectedForms.length === 0) {
        setAvailableQuestions([]);
        return;
      }
  
      try {
        const formIds = selectedForms.map(form => form.id);
        const q = query(
          collection(db, 'questions'),
          where('questionFormId', 'in', formIds)
        );
        const querySnapshot = await getDocs(q);
        
        // Form ID'ye göre grupla ve sırala
        const questionsByForm = {};
        querySnapshot.docs.forEach(doc => {
          const data = doc.data();
          if (!questionsByForm[data.questionFormId]) {
            questionsByForm[data.questionFormId] = [];
          }
          questionsByForm[data.questionFormId].push({
            text: data.text,
            sortOrder: data.sortOrder
          });
        });
  
        // Her formun sorularını sırala
        Object.values(questionsByForm).forEach(questions => {
          questions.sort((a, b) => a.sortOrder - b.sortOrder);
        });
  
        // Seçili form sırasına göre metinleri topla
        const orderedTexts = [];
        selectedForms.forEach(form => {
          const formQuestions = questionsByForm[form.id] || [];
          orderedTexts.push(...formQuestions.map(q => q.text));
        });
  
        // Tekilleri al ama sırayı koru
        const uniqueQuestions = [...new Set(orderedTexts)];
        setAvailableQuestions(uniqueQuestions);
      } catch (error) {
        console.error('Sorular alınırken hata:', error);
        setSnackbar({
          open: true,
          message: t('aiReporting.questionsFetchError'),
          severity: 'error'
        });
      }
    }
  
    fetchQuestions();
  }, [selectedForms, t]);
  

  // --------------------------------------------------------------------------
  // AI_sum_text oluşturma
  // --------------------------------------------------------------------------
  useEffect(() => {
    const combinedText = filteredRawRows
      .map((row) => {
        const questionPlain = stripHtmlTags(row.questionText || '')
        const tagsString = row.userTags?.length
          ? `(tags: ${row.userTags.join(', ')})`
          : ''
        return `KANAL: ${row.channelName}
FORM: ${row.formName}
USER: ${row.userNickname} ${tagsString}
Soru: ${questionPlain}
Cevap: ${row.textAnswer}
---
`
      })
      .join('\n')

    setAISumText(combinedText)
  }, [filteredRawRows])

  // --------------------------------------------------------------------------
  // "Reddet" Butonu (isteğe bağlı)
  // --------------------------------------------------------------------------
  const handleReject = async (responseDocId) => {
    if (!responseDocId) return
    try {
      await updateDoc(doc(db, 'responses', responseDocId), {
        status: 'inProgress',
        completedAt: deleteField(),
        finalAnswers: deleteField(),
        answers: deleteField()
      })

      setRawRows((prev) => prev.filter((row) => row.docId !== responseDocId))
      setSnackbar({
        open: true,
        message: t('aiReporting.rejectSuccess'),
        severity: 'success'
      })
    } catch (err) {
      console.error('Reddetme işleminde hata:', err)
      setSnackbar({
        open: true,
        message: t('aiReporting.rejectError'),
        severity: 'error'
      })
    }
  }

  // --------------------------------------------------------------------------
  // AI API'ye gönder
  // --------------------------------------------------------------------------
  const handleSendToAI = async () => {
    if (!AIQuestion || !AISumText) {
      setSnackbar({
        open: true,
        message: t('aiReporting.noAIQuestionOrTextWarning'),
        severity: 'error'
      })
      return
    }

    try {
      setApiLoading(true)
      const response = await fetch(
        'https://europe-west3-evosurvey.cloudfunctions.net/qip-evoo-comminity-report',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            AI_question: AIQuestion,
            AI_sum_text: AISumText
          })
        }
      )
      const data = await response.json()
      setAIReport(JSON.stringify(data, null, 2))
      setSnackbar({
        open: true,
        message: t('aiReporting.aiAPISuccess'),
        severity: 'success'
      })
    } catch (err) {
      console.error('API isteğinde hata:', err)
      setSnackbar({
        open: true,
        message: t('aiReporting.aiAPIError'),
        severity: 'error'
      })
    } finally {
      setApiLoading(false)
    }
  }

  const getReportText = () => {
    try {
      const parsed = JSON.parse(AIReport);
      if (parsed && typeof parsed.text === 'string') {
        return parsed.text;
      }
    } catch (err) {
      // JSON değilse veya 'text' alanı yoksa
    }
    return AIReport;
  };

  const handleCopyToClipboard = async () => {
    const text = getReportText();
    try {
      await navigator.clipboard.writeText(text);
      setSnackbar({
        open: true,
        message: t('aiReporting.copiedToClipboard'),
        severity: 'success',
      });
    } catch (err) {
      console.error('Panoya kopyalama başarısız:', err);
      setSnackbar({
        open: true,
        message: t('aiReporting.copyToClipboardError'),
        severity: 'error',
      });
    }
  };

  const handleSaveToDocx = async () => {
    const text = getReportText();
    const lines = text.split('\n');
    const paragraphs = lines.map((line) => new Paragraph(line));
  
    const doc = new Document({
      sections: [
        {
          properties: {},
          children: paragraphs,
        },
      ],
    });
  
    try {
      const blob = await Packer.toBlob(doc);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'AI_Report.docx';
      link.click();
      URL.revokeObjectURL(url);
      setSnackbar({
        open: true,
        message: t('aiReporting.documentSaved'),
        severity: 'success',
      });
    } catch (err) {
      console.error('Docx kaydetme başarısız:', err);
      setSnackbar({
        open: true,
        message: t('aiReporting.saveToDocxError'),
        severity: 'error',
      });
    }
  };

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------
  return (
    <Container
      sx={{
        my: 2,
        py: 2,
        mt: 4,
        backgroundColor: '#f9f9f9',
        padding: 4,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        borderRadius: 2
      }}
    >
      <Typography variant="h4" gutterBottom align="center" sx={{ color: '#3f51b5' }}>
        <CommentIcon sx={{ mr: 1 }} />
        {t('aiReporting.pageTitle')}
      </Typography>
      {/* 1) WORKSPACE Seçimi */}
      <Paper
        variant="outlined"
        sx={{
          p: 2,
          mb: 2,
          width: '100%',
          maxWidth: 900,
          backgroundColor: '#fff'
        }}
      >
        <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
          {t('aiReporting.selectWorkspaceTitle')}
        </Typography>
        <FormControl fullWidth size="small">
          <InputLabel id="workspace-select-label">Workspace</InputLabel>
          <Select
            labelId="workspace-select-label"
            label="Workspace"
            value={selectedWorkspace?.id || ''}
            onChange={(e) => {
              const wsId = e.target.value
              const wsObj = workspaces.find((w) => w.id === wsId) || null
              setSelectedWorkspace(wsObj)
              setSelectedChannels([])
              setSelectedForms([])
              setAIReport('')
            }}
          >
            <MenuItem value="">{t('aiReporting.noWorkspaceSelected')}</MenuItem>
            {workspaceLoading ? (
              <MenuItem disabled>{t('aiReporting.workspaceLoading')}</MenuItem>
            ) : (
              workspaces.map((ws) => (
                <MenuItem key={ws.id} value={ws.id}>
                  {ws.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Paper>

      {/* 2) Kanal / Form / Tag Filtre */}
      {selectedWorkspace && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            width: '100%',
            maxWidth: 900,
            backgroundColor: '#fff'
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
            {t('aiReporting.channelsFormsFiltersTitle')}
          </Typography>
          <Stack spacing={2}>
            {/* Kanal Çoklu Seçimi */}
            <Autocomplete
              multiple
              options={channels}
              value={selectedChannels}
              onChange={(_, newValue) => {
                setSelectedChannels(newValue)
                setSelectedForms([])
                setAIReport('')
              }}
              getOptionLabel={(option) => option.name || option.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('aiReporting.channelsLabel')}
                  placeholder={t('aiReporting.channelsPlaceholder')}
                  size="small"
                />
              )}
            />

            {/* Form Çoklu Seçimi */}
            <Autocomplete
              multiple
              options={questionForms}
              value={selectedForms}
              onChange={(_, newValue) => {
                setSelectedForms(newValue)
                setAIReport('')
              }}
              getOptionLabel={(option) => option.name || option.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('aiReporting.formsLabel')}
                  placeholder={t('aiReporting.formsPlaceholder')}
                  size="small"
                />
              )}
            />
            {/* Soru Filtresi */}
            {availableQuestions.length > 0 && (
              <Autocomplete
                multiple
                options={availableQuestions}
                value={selectedQuestions}
                onChange={(event, newValue) => {
                  // Seçilenleri availableQuestions sırasına göre sırala
                  const sorted = newValue.slice().sort((a, b) => 
                    availableQuestions.indexOf(a) - availableQuestions.indexOf(b)
                  );
                  setSelectedQuestions(sorted);
                }}
                getOptionLabel={(option) => stripHtmlTags(option)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('aiReporting.filterByQuestions')}
                    placeholder={t('aiReporting.selectQuestions')}
                    size="small"
                  />
                )}
              />
            )}
            {/* Tag Filtre */}
            {availableTags.length > 0 && (
              <Autocomplete
                multiple
                options={availableTags}
                value={selectedTags}
                onChange={(event, newValue) => setSelectedTags(newValue)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('aiReporting.tagLabel')}
                    placeholder={t('aiReporting.tagPlaceholder')}
                    size="small"
                  />
                )}
              />
            )}
          </Stack>
        </Paper>
      )}

      {/* 3) AI Parametreleri */}
      {selectedForms.length > 0 && (
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            width: '100%',
            maxWidth: 900,
            backgroundColor: '#fff'
          }}
        >
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold' }}>
            {t('aiReporting.pageTitle')}
          </Typography>
          <Stack spacing={2}>
            <TextField
              label={t('aiReporting.aiQuestionLabel')}
              size="small"
              multiline
              rows={2}
              value={AIQuestion}
              onChange={(e) => setAIQuestion(e.target.value)}
            />
            <Button variant="contained" onClick={handleSendToAI}>
              {t('aiReporting.analyzeAI')}
            </Button>
          </Stack>
        </Paper>
      )}

      {/* 4) AI raporunu göster */}
      {apiLoading ? (
        <Box textAlign="center" sx={{ mt: 2 }}>
          <CircularProgress />
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('aiReporting.waitingAIDescription')}
          </Typography>
        </Box>
      ) : (
        AIReport && (
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              mb: 2,
              width: '100%',
              maxWidth: 900,
              backgroundColor: '#fff'
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
              {t('aiReporting.aiReportTitle')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            {(() => {
              // JSON parse denemesi
              let parsedReport
              try {
                parsedReport = JSON.parse(AIReport)
              } catch (err) {
                parsedReport = null
              }

              if (parsedReport && typeof parsedReport.text === 'string') {
                return (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {parsedReport.text}
                  </Typography>
                )
              }
              // yoksa ham göster
              return (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'gray' }}>
                  {AIReport}
                </Typography>
              )
            })()}
            <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={handleCopyToClipboard}>
                {t('aiReporting.copyToClipboard')}
              </Button>
              <Button variant="outlined" onClick={handleSaveToDocx}>
                {t('aiReporting.saveToDocx')}
              </Button>
            </Stack>
          </Paper>
        )
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}